import { Link } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { Icon } from '@iconify/react/dist/iconify.js';
import { getDepartmentIcon, getDepartmentColor, getStatsDepartments, getDepartmentName, getDepartmentId } from '../../core/constants/departments';
import { useDepartments } from '../../core/DepartmentsContext';
import { useMemo } from 'react';
import "../../styles/userlist.css";
import "../../styles/general.css";
import "../../styles/main.css";
import "../../styles/CompareVolunteers.css";
import Avatar from '../Avatar';

// Register the required chart components
Chart.register(ArcElement, Tooltip, Legend);

const CHART_BACKGROUND_COLORS = [
  '#762b8c', // Purple
  '#73c76f', // Green
  '#3ec7ed', // Cyan
  '#c76fbe', // Pink
  '#eba536', // Orange
  '#9e6fc7', // Lavender
  '#ffee70', // Yellow
];

const CHART_BORDER_COLORS = [
  '#5a2170', // Dimmer Purple
  '#58a356', // Dimmer Green
  '#2ea0c0', // Dimmer Cyan
  '#a05599', // Dimmer Pink
  '#c4862c', // Dimmer Orange
  '#7f58a1', // Dimmer Lavender
  '#d9c95f', // Dimmer Yellow
];

const SardineStatsCard = ({ sardine, onVote, isVoted }) => {
  const { departments: catalog } = useDepartments();
  const statsConfig = useMemo(() => getStatsDepartments(catalog), [catalog]);

  const getChartData = (stats) => {
    const chartValues = [
      ...statsConfig.named.map((dept) => stats[dept.statsKey] ?? 0),
      stats[statsConfig.otherStatsKey] ?? 0,
    ];
    const allZero = chartValues.every((value) => value === 0);

    // If all values are zero, show an empty chart
    if (allZero) {
      return {
        labels: ['No Events'],
        datasets: [
          {
            data: [1],
            backgroundColor: ['#cccccc'],
            borderColor: ['#bbbbbb'],
            borderWidth: 1,
          },
        ],
      };
    }

    // Otherwise, show the actual chart
    return {
      labels: statsConfig.chartLabels,
      datasets: [
        {
          data: chartValues,
          backgroundColor: CHART_BACKGROUND_COLORS,
          borderColor: CHART_BORDER_COLORS,
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = (totalEvents) => ({
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: {
          boxWidth: 15,
          padding: 15
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.raw}`;
          }
        }
      }
    },
    animation: {
      duration: 600, // faster animation (default is 1000ms)
      easing: 'easeOutCubic' // optional: controls animation curve
    },
    cutout: '60%',
    // Center text plugin
    elements: {
      center: {
        text: `${totalEvents}`,
        color: '#212529',
        fontStyle: 'sharetechmono-regular',
        sidePadding: 20,
        minFontSize: 18,
        lineHeight: 25
      }
    }
  });

  // Plugin to display text in the center of the doughnut
  const centerTextPlugin = {
    id: 'centerText',
    beforeDraw: function(chart) {
      if (chart.config.options.elements && chart.config.options.elements.center) {
        const ctx = chart.ctx;
        const centerConfig = chart.config.options.elements.center;
        const fontStyle = centerConfig.fontStyle || 'Arial';
        const txt = centerConfig.text;
        const color = centerConfig.color || '#fff';
        ctx.font = "30px " + fontStyle;

        // Get center X & Y
        const centerX = ((chart.chartArea.left + chart.chartArea.right) / 2);
        const centerY = ((chart.chartArea.top + chart.chartArea.bottom) / 2);

        // Draw text in center
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = color;
        ctx.font = '500 24px ' + fontStyle;
        ctx.fillText(txt, centerX, centerY);
      }
    }
  };

  // Register the plugin
  Chart.register(centerTextPlugin);


  // Minimum requirements: Newbies
  const isNewbie = sardine.membershipType === "Newbie";
  const staffConditionFailsN = sardine.stats.eventsAsStaff < 2;

  // Minimum requirements: Juniores
  const isJunior = sardine.membershipType === "Junior";

  const approvedNow = 0 >= 2 || 1 >= 3;

  const membershipType = sardine.membershipType || 'Newbie';
  const displayName = [
    sardine.firstLastName || sardine.fullName || sardine.name,
    sardine.nickname ? `"${sardine.nickname}"` : '',
  ].filter(Boolean).join(' ');

return (
    <div
      className={`volunteer-card ${isVoted ? 'selected' : ''}`}
      style={{ '--department-color': getDepartmentColor(sardine.departments?.[0]) }}
    >
      <div className="volunteer-info compare-sardine-user-info">
        <div className="user-item-avatar-wrapper">
          <Link to={`/users/${sardine._id}`}>
            <Avatar user={sardine} size={100} style={{ border: '2px solid rgba(0,0,0,0.6)' }} />
          </Link>
        </div>

        <p className="user-item-name compare-sardine-user-name">
          {displayName}

          {isNewbie && staffConditionFailsN && (
            <span className="mr-tooltip-wrapper">
              <Icon
                icon={"ph:seal-warning-fill"}
                className="mr-icon"
                aria-label="Minimum requirements not achieved"
              />
              <span className="mr-tooltip">
                {"Not enough events as Staff. Need 2."}
              </span>
            </span>
          )}

          {isJunior && !approvedNow && (
            <span className="mr-tooltip-wrapper">
              <Icon
                icon={"ph:seal-warning-fill"}
                className="mr-icon"
                aria-label="Minimum requirements not achieved"
              />
              <span className="mr-tooltip">
                {"Not enough events as Staff. Need 2 or 3."}
              </span>
            </span>
          )}
        </p>

        <div className="user-item-status">
          <p className="user-item-position">{sardine.position} · {membershipType}</p>
        </div>

        <p className="user-item-departments">
          {sardine.departments?.map((department, index) => (
            <span
              key={getDepartmentId(department) || index}
              className="user-item-department"
              style={{ '--department-color': getDepartmentColor(department, catalog) }}
            >
              <div className="dep-icon">{getDepartmentIcon(department, {}, catalog)}</div>
              <div className="dep-name">{getDepartmentName(department, catalog)}</div>
            </span>
          ))}
        </p>

        <div className="compare-sardine-info-divider" aria-hidden="true" />
      </div>

        <div className="circle-charts" style={{ display: "flex" }}>
          <div className="chart-container">
            <Doughnut data={getChartData(sardine.stats)} options={chartOptions(sardine.stats.totalEvents)} />
          </div>
        </div>

        <div className="stats-container">
          <div className="stat-row">
            <span><Icon icon="fluent-mdl2:party-leader" /> Events as MO:</span>
            <span>{sardine.stats.eventsAsMO}</span>
          </div>
          <div className="stat-row">
            <span><Icon icon="carbon:events" /> Events as VMO:</span>
            <span>{sardine.stats.eventsAsVMO}</span>
          </div>
          <div className="stat-row">
            <span><Icon icon="mdi:account-group" /> Events as Staff:</span>
            <span>{sardine.stats.eventsAsStaff}</span>
          </div>
          <div className="stat-row">
            <span><Icon icon="ant-design:number-outlined" /> Total Events:</span>
            <span>{sardine.stats.totalEvents}</span>
          </div>
          <div className="stat-row">
            <span><Icon icon="nimbus:shot" /> Shots Won/Sent:</span>
            <span>{sardine.stats.shotsWon}/{sardine.stats.shotsSent}</span>
          </div>
        </div>

        {onVote && sardine.openCallComment && (
          <div className="sardine-opencall-comment">
            <Icon icon="mdi:comment-text-outline" style={{marginRight: "6px", verticalAlign: "middle"}} />
            {sardine.openCallComment}
          </div>
        )}

        {onVote && <button className={`vote-button ${isVoted ? 'selected' : ''}`} onClick={() => onVote(sardine._id)}><Icon icon="ant-design:check-circle-filled" />{isVoted ? 'Selected' : 'Vote'}</button>}
    </div>
  );
};

export default SardineStatsCard;
